import type { ReactNode } from 'react';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    'w-fit inline-flex items-center justify-center gap-[6px] cursor-pointer',
    'border border-transparent rounded select-none',
    'font-sans whitespace-nowrap transition-colors',
    'active:outline-none active:ring',
    'focus-visible:outline-none',
    'disabled:pointer-events-none disabled:border-grayscale-200 disabled:bg-grayscale-50',
    'data-[disabled=true]:pointer-events-none data-[disabled=true]:border-grayscale-200 data-[disabled=true]:bg-grayscale-50',
  ],
  {
    variants: {
      variant: {
        contain: [
          'disabled:text-grayscale-400',
          'focus-visible:ring-offset-2 focus-visible:ring-1 focus-visible:ring-primary-600',
        ],
        outline: [
          'border-grayscale-100',
          'disabled:text-grayscale-300',
          'focus-visible:ring-4 focus-visible:ring-[hsla(var(--primary),0.1)] focus-visible:border-primary-400',
        ],
        link: [
          'inline-block p-0 h-[unset] bg-transparent',
          'underline-offset-4 hover:underline',
          'data-[disabled=true]:pointer-events-none data-[disabled=true]:bg-transparent data-[disabled=true]:border-transparent data-[disabled=true]:text-grayscale-400',
        ],
        ghost: ['border-transparent'],
      },
      size: {
        default: 'h-11 px-4 py-2 text-sm leading-6 font-medium',
        lg: 'h-12 px-5 py-3 text-sm leading-7 font-medium',
        icon: 'h-11 w-11',
      },
      intent: {
        primary: [
          'bg-primary text-primary-foreground',
          'hover:bg-primary-600',
          'active:bg-primary-600 active:ring-[hsla(var(--primary-600),0.1)]',
          'focus-visible:ring-primary-600',
        ],
        secondary: [
          'border border-gray-200 text-gray-700',
          'hover:bg-gray-100',
          'active:bg-gray-200 active:ring-[hsla(var(--gray-300),0.1)]',
          'focus-visible:ring-[hsla(var(--gray-300),0.2)]',
        ],
        tertiary: [
          'bg-white text-grayscale-900',
          'hover:bg-grayscale-50',
          'active:bg-grayscale-50 active:ring-[hsla(var(--grayscale-300),0.1)]',
          'focus-visible:ring-[hsla(var(--primary),0.1)] focus-visible:border-primary-400',
        ],
        danger: [
          'bg-error text-error-foreground',
          'hover:bg-error-600',
          'active:bg-error-600 active:ring-[hsla(var(--error-600),0.1)]',
          'focus-visible:ring-[hsla(var(--error),0.1)] focus-visible:border-error-400',
        ],
        success: [
          'bg-success text-success-foreground',
          'hover:bg-success-600',
          'active:bg-success-600 active:ring-[hsla(var(--success-600),0.1)]',
          'focus-visible:ring-[hsla(var(--success),0.1)] focus-visible:border-success-400',
        ],
      },
    },
    compoundVariants: [
      {
        variant: 'link',
        intent: 'primary',
        className: [
          'inline-block p-0 h-[unset] bg-transparent text-primary',
          'hover:bg-transparent',
          'active:text-primary-600 active:bg-transparent active:ring-0 active:underline',
          'focus-visible:bg-transparent focus-visible:ring-0 focus-visible:underline',
        ],
      },
      {
        variant: 'link',
        intent: 'tertiary',
        className: [
          'inline-block p-0 h-[unset] bg-transparent text-grayscale',
          'hover:bg-transparent',
          'active:text-grayscale-600 active:bg-transparent active:ring-0 active:underline',
          'focus-visible:bg-transparent focus-visible:ring-0 focus-visible:underline',
        ],
      },
      {
        variant: 'link',
        intent: 'danger',
        className: [
          'inline-block p-0 h-[unset] bg-transparent text-error',
          'hover:bg-transparent',
          'active:text-error-600 active:bg-transparent active:ring-0 active:underline',
          'focus-visible:bg-transparent focus-visible:ring-0 focus-visible:underline',
        ],
      },
      {
        variant: 'outline',
        intent: 'primary',
        className: [
          'bg-primary-50 border-primary-200 text-primary',
          'hover:bg-primary-200',
          'active:bg-primary-200 active:ring-[hsla(var(--primary-300),0.1)]',
          'focus-visible:ring-[hsla(var(--primary),0.1)] focus-visible:border-primary-400',
        ],
      },
      {
        variant: 'outline',
        intent: 'tertiary',
        className: [
          'bg-white text-grayscale-900',
          'hover:bg-grayscale-50',
          'active:bg-grayscale-50 active:ring-[hsla(var(--grayscale-300),0.1)]',
          'focus-visible:ring-[hsla(var(--primary),0.1)] focus-visible:border-primary-400',
        ],
      },
      {
        variant: 'outline',
        intent: 'danger',
        className: [
          'bg-error-50 border-error-200 text-error',
          'hover:bg-error-100',
          'active:bg-error-100 active:ring-[hsla(var(--error-300),0.1)]',
          'focus-visible:ring-[hsla(var (--error),0.1)] focus-visible:border-error-400',
        ],
      },
      {
        variant: 'outline',
        intent: 'success',
        className: [
          'bg-success-50 border-success-200 text-success',
          'hover:bg-success-100',
          'active:bg-success-100 active:ring-[hsla(var(--success-300),0.1)]',
          'focus-visible:ring-[hsla(var(--success),0.1)] focus-visible:border-success-400',
        ],
      },
      {
        variant: 'ghost',
        intent: 'tertiary',
        className: [
          'bg-white text-grayscale',
          'hover:bg-grayscale-50',
          'active:bg-grayscale-100 active:text-grayscale-600 active:ring-transparent',
          'focus-visible:ring-[hsla(var(--grayscale-300),0.1)]',
        ],
      },
      {
        variant: 'ghost',
        intent: 'primary',
        className: [
          'bg-transparent text-primary',
          'hover:bg-primary-50',
          'active:bg-primary-100 active:text-primary-600 active:ring-transparent',
          'focus-visible:bg-primary-100 focus-visible:ring-[hsla(var(--primary-300),0.1)]',
        ],
      },
    ],
    defaultVariants: {
      size: 'default',
      variant: 'contain',
      intent: 'primary',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      intent,
      variant,
      size,
      asChild = false,
      startIcon,
      endIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            intent,
            className,
          })
        )}
        data-disabled={props.disabled}
        tabIndex={props.disabled ? -1 : 0}
        {...props}
        ref={ref}
      >
        {startIcon && <span className="inline-flex">{startIcon}</span>}

        <Slottable>
          {typeof children === 'string' ? <span>{children}</span> : children}
        </Slottable>

        {endIcon && <span className="inline-flex">{endIcon}</span>}
      </Comp>
    );
  }
);

Button.displayName = 'Button';

export { Button, buttonVariants };
